import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { combineLatest, map, switchMap } from 'rxjs';
import { take,tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { ApolloClientService } from 'src/app/services/api/apollo-client.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { RouteConstants } from 'src/app/constants/routeConstants';
import { environment, links } from 'src/environments/environment';
import { APP_VERSION } from 'src/app/version';
import { AuthService } from 'src/app/services/auth.service';
import { menubarHeaderDesignToken } from 'src/app/primeng-design-tokens/menubar-header';
import { menuDesignToken } from 'src/app/primeng-design-tokens/menu';
import { drawerDesignToken } from 'src/app/primeng-design-tokens/drawer';
import { ThemeService } from 'src/app/services/theme.service';
import { Theme } from 'src/app/enums/theme.enum';
import { OrganizationalGroupsApiService } from 'src/app/services/api/organizational-groups-api.service';
import { BrowserAnalyticsService } from 'src/app/services/browser-analytics.service';
import { LaunchdarklyService } from 'src/app/services/api/launchdarkly.service';
import { SIGN_OUT } from 'src/app/graphQL/mutations/authentication-mutations';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        MenubarModule,
        ButtonModule,
        TieredMenuModule,
        ToggleSwitchModule,
        TooltipModule,
        DrawerModule,
        DialogModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  private orgGroupsService = inject(OrganizationalGroupsApiService);
  private analytics = inject(BrowserAnalyticsService);
  private ldService = inject(LaunchdarklyService);
  private apolloClientService = inject(ApolloClientService);
  private messageService = inject(MessageService);



  @ViewChild('menu') menu: TieredMenu;

  showUserDetails = false;
  showPrivacyNotice = false;
  showAbout = false;
  appVersion = APP_VERSION;

  /**
   * Shared analytics transparency-notice text, sourced from the Fenix Web Analytics SDK
   * (window.AnalyticsSDK.NOTICE) so every app shows identical wording. Informational only —
   * no consent is collected. Undefined if the SDK has not loaded.
   */
  get privacyNotice() {
    return window.AnalyticsSDK?.NOTICE;
  }

  menubarHeaderDesignToken = menubarHeaderDesignToken;
  menuDesignToken = menuDesignToken;
  drawerDesignToken = drawerDesignToken;

  navigationItems: MenuItem[] = [
    {
      label: 'Test Items',
      items: [
        { label: 'Test Analyses', routerLink: RouteConstants.TEST_ANALYSIS },
        { label: 'Test Cases', routerLink: RouteConstants.TEST_CASES },
        { label: 'Test Suites', routerLink: RouteConstants.TEST_SUITES },
        { label: 'Test Specifications', routerLink: RouteConstants.TEST_SPECIFICATIONS },
      ]
    },
    {
      label: 'Coverages',
      items: [
        { label: 'EESE coverage', routerLink: RouteConstants.EESE_COVERAGE },
        { label: 'Specification Coverages', routerLink: RouteConstants.SPECIFICATION_COVERAGE },
        { label: 'Software Version Coverages', routerLink: RouteConstants.SOFTWARE_VERSION_COVERAGE },
        { label: 'RMS Specification Comparison', routerLink: RouteConstants.RMS_SPECIFICATIONS_COMPARISON },
      ]
    },
    {
      label: 'Review',
      routerLink: RouteConstants.REVIEW,
    },
  ];

  menuItems$ = this.authService.userData$.pipe(
    map(userData => [
      {
        id: 'user',
        label: (userData.firstName && userData.lastName)
          ? `${userData.firstName} ${userData.lastName}`
          : userData.userName || '',
        icon: 'pi pi-user',
        visible: !!userData.sessionUID,
        data: {
          userId: userData.userName,
          showTooltip: !!(userData.firstName && userData.lastName),
        },
        command: () => { this.showUserDetails = true; },
      },
      {
        id: 'darkMode',
        label: 'Theme',
      },
      {
        icon: 'pi pi-question-circle',
        label: 'Support',
        tooltip: 'Contact the support team on Microsoft Teams',
        command: () => { this.analytics.track('header_external_link_clicked', { label: 'Support' }); window.open('https://teams.microsoft.com/l/team/19%3Af0563924f10948e29b24c3580bfa34a6%40thread.tacv2/conversations?groupId=34884b53-40a8-4388-b61a-a801abce4000&tenantId=3bc062e4-ac9d-4c17-b4dd-3aad637ff1ac', '_blank'); },
      },
      {
        icon: 'pi pi-book',
        label: 'Wiki',
        tooltip: 'Open the Fenix Configurator documentation',
        command: () => { this.analytics.track('header_external_link_clicked', { label: 'Wiki' }); window.open('https://confluence.collaborationlayer-traton.com/pages/viewpage.action?pageId=88982021&spaceKey=TW&title=4%2BFenix%2BConfigurator', '_blank'); },
      },
      {
        icon: 'pi pi-megaphone',
        label: 'Blog & News',
        tooltip: 'Read about the updates and announcements',
        visible: !environment.IsHero,
        command: () => { this.analytics.track('header_external_link_clicked', { label: 'Blog & News' }); window.open(links.blogAndNewsUrl, '_blank'); },
      },
      {
        icon: 'pi pi-home',
        label: 'Test Tools',
        tooltip: 'Go to the Test Tools portal',
        command: () => { this.analytics.track('header_external_link_clicked', { label: 'Test Tools' }); window.open(links.testToolsUrl, '_blank'); },
      },
      {
        icon: 'pi pi-shield',
        label: 'Privacy & Data',
        tooltip: 'How we use user data in this application',
        visible: !environment.IsHero,
        command: () => { this.showPrivacyNotice = true; },
      },
      {
        icon: 'pi pi-info-circle',
        label: 'About',
        tooltip: 'Application version',
        command: () => { this.showAbout = true; },
      },
      {
        label: userData.sessionUID ? 'Sign out' : 'Sign in',
        icon: userData.sessionUID ? 'pi pi-sign-out' : 'pi pi-sign-in',
        command: () => { userData.sessionUID ? this.signOut() : this.authService.login(); },
        
      },
    ] as MenuItem[])
  );

  testAnalysisRoute = RouteConstants.TEST_ANALYSIS;

  private readonly DEV_GROUP_ID = 'e916b2aa-413a-4f9c-ac20-8380f667e615';

  flagChips$ = combineLatest([
    this.ldService.launchDarklyFlagsWithFallback$,
    this.authService.userData$,
  ]).pipe(
    map(([flags, userData]) => {
      const groups = userData.functionalGroups ?? [];
      if (!groups.includes(this.DEV_GROUP_ID)) return [];
      return [
        { label: 'Migration', value: flags['fenix-migration-mode'] ?? '—' },
        { label: 'DB', value: flags['db-provider'] ?? '—' },
      ];
    })
  );

  aboutFlags$ = this.ldService.launchDarklyFlagsWithFallback$.pipe(
    map(flags => [
      { label: 'Migration', value: flags['fenix-migration-mode'] ?? '—' },
      { label: 'DB', value: flags['db-provider'] ?? '—' },
    ])
  );

  isDark$ =this.themeService.theme$.pipe(map(theme => theme === Theme.Dark));

  userFunctionalGroups$ = this.authService.userData$.pipe(
    switchMap(userData =>
      this.orgGroupsService.get().pipe(
        map(allGroups =>
          allGroups.filter(g => (userData.functionalGroups ?? []).includes(g.uid))
        )
      )
    )
  );
  

  onMenuShow(): void {
    // PrimeNG absolutePosition adds window.scrollY to top, which breaks sticky-header popup placement.
    // After alignOverlay runs, switch to fixed and recompute from viewport-relative getBoundingClientRect().
    requestAnimationFrame(() => {
      const container = this.menu?.container;
      const target = this.menu?.target;
      if (!container || !target) return;
      const rect = target.getBoundingClientRect();
      const menuHeight = container.offsetHeight;
      const menuWidth = container.offsetWidth;
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= menuHeight || spaceBelow >= rect.top
        ? rect.bottom + gap
        : rect.top - menuHeight - gap;
      container.style.position = 'fixed';
      container.style.marginTop = '0';
      container.style.top = top + 'px';
      container.style.left = Math.max(0, rect.right - menuWidth) + 'px';
    });
  }

  public signOut(): void {
    this.apolloClientService.authLayerClient.pipe(
      take(1),
      switchMap(client =>
        client.mutate<{ signOut: boolean | null }>({ mutation: SIGN_OUT })
      ),
      map(response => response ?? false),
      tap(response => {
        if (response) {
          this.messageService.add({ severity: 'success', detail: 'Successfully signed out' });
          this.authService.clearSession();
        } else {
          throw new Error('Failed when signing out');
        }
      }),
      catchError(error => {
        this.messageService.add({ severity: 'error', summary: 'Error when signing out', detail: error });
        console.error('Error signing out:', error);
        this.authService.clearSession();
        return of(undefined);
      }),
    ).subscribe();
  }

  

  constructor() {}

  ngOnInit(): void {}
}
